import React, { useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  infectiveRhinitisDurationEnum,
  infectiveRhinitisSymptomsEnum,
  chronicRhinosinusitisSymptomsEnum,
} from "./data";

import { updateClinicalService } from "../../../../../redux/modules/clinical-services/actions";
import Medications from "../../medications/medications";
import Steps from "../../Steps";
import {
  AlertConfirmationTypes,
  MinorAilmentStep,
} from "../../../../../helpers/constant";
import SubNavigation from "../../common/sub-navigation";
import NewToolTip from "../../../../common/New-ToolTip";
import ChartNav from "../../../../common/ChartNav";
import EndServiceModel from "../../../../common/EndServiceModel";
import Button from "../../../../common/Button";
import { getValidStep, updateStepsValidity } from "../../../text/data";
import { setClinicalServiceSteps } from "../../../../../redux/modules/clinical-services/actions";

const Infective = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const [clinicalAnalysis, setClinicalAnalysis] = useState(false);
  const [showNoInfectiveIdentified, setShowNoInfectiveIdentified] =
    useState(false);
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id: patientId, serviceId } = useParams();
  const dispatch = useDispatch();

  const [step1Val, setStep1Val] = useState(null);
  const [step2Vals, setStep2Vals] = useState([]);
  const [step3Vals, setStep3Vals] = useState([]);
  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);

  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [completeSSAlert, setCompleteSSAlert] = useState(false);

  //Red Flag Alerts
  const [openMultiConditionDialog, setOpenMultiConditionDialog] =
    useState(false);
  const [continueService, setContinueService] = useState(false);
  //Red Flag Alert Input
  const [alertContent, setAlertContent] = useState("");
  const [clickedEndService, setClickedEndService] = useState(false);
  const [alertConfirmation, setAlertConfirmation] = useState(0);
  const [alertExplanation, setAlertExplanation] = useState("");

  //state management
  useEffect(() => {
    if (clickedEndService) {
      onNext();
      setClickedEndService(false);
    }
  }, [clinicalServiceSteps, clickedEndService]);

  // Meant for saving values between screens
  useEffect(() => {
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        .signsAndSymptoms !== null &&
      (clinicalServiceObj.initialAssessment.initialAssessmentForm
        .signsAndSymptoms.durationOfSymptoms !== null ||
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          .signsAndSymptoms.infectiveRhinosinusitisSymptoms !== null ||
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          .signsAndSymptoms.chronicRhinosinusitisDuration !== null ||
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          .signsAndSymptoms.chronicRhinosinusitisSymptoms !== null)
    ) {
      setStep1Val(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms.durationOfSymptoms
      );
      setStep2Vals(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms.infectiveRhinosinusitisSymptoms?.length > 0
          ? clinicalServiceObj.initialAssessment.initialAssessmentForm
              ?.signsAndSymptoms.infectiveRhinosinusitisSymptoms
          : []
      );
      setStep3Vals(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms.chronicRhinosinusitisSymptoms?.length > 0
          ? clinicalServiceObj.initialAssessment.initialAssessmentForm
              ?.signsAndSymptoms.chronicRhinosinusitisSymptoms
          : []
      );
      setClinicalAnalysis(true);
      setCompleteSSAlert(true);
    }
  }, [clinicalServiceObj]);

  // Where to show end card
  useEffect(() => {
    if (step1Val === "none") {
      setCompleteSubStep(true);
    } else if (step1Val === "sevenToTenDays") {
      if (step2Vals?.length > 0) {
        setCompleteSubStep(true);
      } else {
        setCompleteSubStep(false);
      }
    } else if (step1Val === "twelveWeeksOrMore") {
      if (step3Vals?.length > 0) {
        setCompleteSubStep(true);
      } else {
        setCompleteSubStep(false);
      }
    } else {
      setCompleteSubStep(false);
    }
  }, [step1Val, step2Vals, step3Vals]);

  const updateStepsVisibility = async (disable = false) => {
    const updatedServiceSteps = updateStepsValidity(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      disable
    );
    await dispatch(setClinicalServiceSteps(updatedServiceSteps));
  };

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );
      const updatedSteps = {
        signsAndSymptoms: {
          durationOfSymptoms: step1Val,
          infectiveConfirmationType: alertConfirmation,
          infectiveExplanation: alertExplanation,
          infectiveRhinosinusitisSymptoms:
            step1Val !== "sevenToTenDays"
              ? []
              : step2Vals.includes("none")
              ? ["none"]
              : step2Vals,
          chronicRhinosinusitisSymptoms:
            step1Val !== "twelveWeeksOrMore"
              ? []
              : step3Vals.includes("none")
              ? ["none"]
              : step3Vals,
        },
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        showReferral: showRef,
        serviceSteps: clinicalServiceSteps,
      };
      dispatch(updateClinicalService(serviceId, updatedSteps));
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  // Multi selections
  const step2MultiSelection = (val) => {
    if (val === "none") {
      !step2Vals.includes(val) ? setStep2Vals(["none"]) : setStep2Vals([]);
    } else {
      if (step2Vals.includes("none")) {
        setStep2Vals([val]);
      } else {
        step2Vals.includes(val)
          ? setStep2Vals(step2Vals.filter((item) => item !== val))
          : setStep2Vals([...step2Vals, val]);
      }
    }
  };

  const step3MultiSelection = (val) => {
    if (val === "none") {
      !step3Vals.includes(val) ? setStep3Vals(["none"]) : setStep3Vals([]);
    } else {
      if (step3Vals.includes("none")) {
        setStep3Vals([val]);
      } else {
        step3Vals.includes(val)
          ? setStep3Vals(step3Vals.filter((item) => item !== val))
          : setStep3Vals([...step3Vals, val]);
      }
    }
  };

  // Review results
  // const handleAnalysis = () => {
  //   if (!continueService) {
  //     if (step1Val === "none") {
  //       setShowNoInfectiveIdentified(true);
  //     } else if (step3Vals.includes("none")) {
  //         setShowNoInfectiveIdentified(true);
  //     } else if (step2Vals.includes("none")) {
  //         setShowNoInfectiveIdentified(true);
  //     } else {
  //       handleAlerts();
  //     }
  //   }
  // };
  const handleAnalysis = () => {
    if (!continueService) {
      if (
        step1Val === "none" ||
        step3Vals.includes("none") ||
        step2Vals.includes("none")
      ) {
        setShowNoInfectiveIdentified(true);
      } else {
        handleAlerts();
      }
    } else {
      handleAlerts();
    }
  };

  const handleAlerts = () => {
    if (step2Vals.length > 0 && !step2Vals.includes("none")) {
      setAlertContent(
        <>
          <p className="font-semibold text-gray-600 text-base">
            Possible Infective Rhinitis
            {step2Vals?.length ? (
              <>
                <br />
                [{step2Vals
                  .map?.(
                    (v) =>
                      infectiveRhinitisSymptomsEnum.options.find(
                        (o) => o.value === v && v !== "none"
                      )?.header.toLowerCase()
                  )
                  ?.join?.(", ")}]
              </>
            ) : (
              ""
            )}
          </p>
        </>
      );
      setOpenMultiConditionDialog(true);
    } else if (step3Vals.length > 0 && !step3Vals.includes("none")) {
      setAlertContent(
        <>
          <p className="font-semibold text-gray-600 text-base">
            Possible Chronic Rhinosinusitis
            {step3Vals?.length ? (
              <>
                <br />
                [{step3Vals
                  .map?.(
                    (v) =>
                      chronicRhinosinusitisSymptomsEnum.options.find(
                        (o) => o.value === v && v !== "none"
                      )?.header.toLowerCase()
                  )
                  ?.join?.(", ")}]
              </>
            ) : (
              ""
            )}
          </p>
        </>
      );
      setOpenMultiConditionDialog(true);
    }
  };

  // If answers get changed at all
  const resetAnalysis = () => {
    setClinicalAnalysis(false);
    setCompleteSubStep(false);
    setContinueService(false);
    setCompleteSSAlert(false);
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="font-bold text-teal-300 text-xs-display">
                  Infective Rhinitis - Signs & Symptoms
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="life-style"
              >
                {/* step1 Duration of Symptoms */}
                <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                  <div
                    className="flex flex-row items-center justify-start px-5 py-4 bg-center bg-cover"
                    style={{
                      backgroundImage: `url(/icons/texture-background-3.png)`,
                    }}
                  >
                    <h3 className="text-xl font-bold text-teal-600">
                      Duration of Symptoms
                    </h3>
                  </div>
                  <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                    <div className="flex flex-col gap-4">
                      <p className="text-lg h-[51px] font-semibold text-gray-800">
                        {infectiveRhinitisDurationEnum.question}
                        <br />
                        <span className="font-normal">
                          {infectiveRhinitisDurationEnum.subquestion}{" "}
                        </span>
                      </p>
                      <div className="flex flex-col gap-2">
                        {infectiveRhinitisDurationEnum.options.map((option) => {
                          return (
                            <>
                              <button
                                onClick={() => {
                                  resetAnalysis();
                                  setStep1Val(option.value);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                      text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                      ${
                                        step1Val === option.value
                                          ? "bg-gray-20 border-gray-600"
                                          : "bg-white border-gray-100"
                                      }`}
                              >
                                <p className="flex items-start justify-start font-semibold text-body-md">
                                  {option.header}{" "}
                                  {option.value === "sevenToTenDays" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="sevenToTen"
                                      />
                                      <NewToolTip
                                        id={"sevenToTen"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                            Consider bacterial, fungal, or viral
                                            causes when an allergen exposure
                                            does not occur.
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                  {option.value === "twelveWeeksOrMore" && (
                                    <>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-5 ml-3"
                                        data-tooltip-id="twelveWeeks"
                                      />
                                      <NewToolTip
                                        id={"twelveWeeks"}
                                        render={() => (
                                          <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                            Consider inflammatory condition of
                                            the sinuses lasting 12 weeks or
                                            longer despite medical management.
                                          </div>
                                        )}
                                      />
                                    </>
                                  )}
                                </p>
                                <p className="flex items-start justify-start font-semibold text-gray-600 text-body-md">
                                  {option.subHeader}
                                </p>
                              </button>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {step1Val === "sevenToTenDays" && (
                  <>
                    {/* step2 Infective Rhinitis - Symptoms */}
                    <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                      <div
                        className="flex flex-row items-center justify-start px-5 py-4 bg-center bg-cover"
                        style={{
                          backgroundImage: `url(/icons/texture-background-3.png)`,
                        }}
                      >
                        <h3 className="text-xl font-bold text-teal-600 ">
                          Infective Rhinitis - Symptoms
                        </h3>
                        <>
                          <ReactSVG
                            src="/icons/information-dark.svg"
                            className="w-5 ml-3"
                            data-tooltip-id="infectiveRhinitis"
                          />
                          <NewToolTip
                            id={"infectiveRhinitis"}
                            render={() => (
                              <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                Associated with upper respiratory tract
                                infection (URTI) symptoms, usually episodic.
                              </div>
                            )}
                          />
                        </>
                      </div>
                      <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                        <div className="flex flex-col gap-4">
                          <p className="text-lg h-[51px] font-semibold text-gray-800">
                            {infectiveRhinitisSymptomsEnum.question}
                            <br />
                            <span className="font-normal">
                              {infectiveRhinitisSymptomsEnum.subquestion}{" "}
                            </span>
                          </p>
                          <div className="flex flex-col gap-2">
                            {infectiveRhinitisSymptomsEnum.options.map(
                              (option) => {
                                return (
                                  <>
                                    <button
                                      onClick={() => {
                                        resetAnalysis();
                                        step2MultiSelection(option.value);
                                        setStep3Vals([]); // reset possible alt path
                                      }}
                                      className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                          text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                          ${
                                            step2Vals?.includes(option.value)
                                              ? "bg-gray-20 border-gray-600"
                                              : "bg-white border-gray-100"
                                          }`}
                                    >
                                      <p className="flex items-start justify-start font-semibold text-body-md">
                                        {option.header}{" "}
                                      </p>
                                      <p className="flex items-start justify-start font-semibold text-gray-600 text-body-md">
                                        {option.subHeader}
                                      </p>
                                    </button>
                                  </>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {step1Val === "twelveWeeksOrMore" && (
                  <>
                    {/* step3 Chronic Rhinosinusitis - Symptoms */}
                    <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                      <div
                        className="flex flex-row items-center justify-start px-5 py-4 bg-center bg-cover"
                        style={{
                          backgroundImage: `url(/icons/texture-background-3.png)`,
                        }}
                      >
                        <h3 className="text-xl font-bold text-teal-600 ">
                          Chronic Rhinosinusitis - Symptoms
                        </h3>
                        <>
                          <ReactSVG
                            src="/icons/information-dark.svg"
                            className="w-5 ml-3"
                            data-tooltip-id="infectiveRhinitis"
                          />
                          <NewToolTip
                            id={"infectiveRhinitis"}
                            render={() => (
                              <div className="!bg-gray-10 max-w-[460px] text-teal-300 text-body-sm p-3 text-left font-normal">
                                Associated with inflammatory condition of the
                                sinuses
                              </div>
                            )}
                          />
                        </>
                      </div>
                      <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                        <div className="flex flex-col gap-4">
                          <p className="text-lg h-[51px] font-semibold text-gray-800">
                            {chronicRhinosinusitisSymptomsEnum.question}
                            <br />
                            <span className="font-normal">
                              {chronicRhinosinusitisSymptomsEnum.subquestion}{" "}
                            </span>
                          </p>
                          <div className="flex flex-col gap-2">
                            {chronicRhinosinusitisSymptomsEnum.options.map(
                              (option) => {
                                return (
                                  <>
                                    <button
                                      onClick={() => {
                                        resetAnalysis();
                                        step3MultiSelection(option.value);
                                        setStep2Vals([]); // reset possible alt path
                                      }}
                                      className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px] text-gray-800 
                                          hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" 
                                          ${
                                            step3Vals?.includes(option.value)
                                              ? "bg-gray-20 border-gray-600"
                                              : "bg-white border-gray-100"
                                          }`}
                                    >
                                      <p className="flex items-start justify-start font-semibold text-body-md">
                                        {option.header}
                                      </p>
                                      <p className="flex items-start justify-start font-semibold text-gray-600 text-body-md">
                                        {option.subHeader}
                                      </p>
                                    </button>
                                  </>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {completeSubStep && (
                  <div>
                    <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                      {/* <div className="flex flex-row justify-start rounded-s">
                        <button
                          className="inline-flex items-center justify-center w-auto h-4 px-2 py-4 m-2 font-semibold text-white transition-all duration-300 border rounded-full bg-REELTeal border-REELTeal hover:bg-teal-300"
                          onClick={() => {
                            setCompleteSubStep(false)
                            setClinicalAnalysis(false);
                            setCompleteSSAlert(false);
                          }}
                        >
                          <ChevronLeftIcon className="w-4 h-4 text-white" />
                        </button> 
                      </div> */}
                      <div className="m-8 mt-6 h-[420px] w-[420px] bg-green-50 rounded-full flex items-center justify-center bg-opacity-40">
                        <ReactSVG
                          src={"/icons/analysis-pad.svg"}
                          className={``}
                        />
                      </div>
                      <div className="my-5 text-center mx-7">
                        {!clinicalAnalysis && (
                          <Button
                            className="w-full !px-2 !text-xl"
                            type="primary"
                            text="Infective Rhinitis Results"
                            onClick={() => {
                              handleAnalysis();
                            }}
                          />
                        )}
                        {clinicalAnalysis && (
                          <h4 className="flex items-center justify-center text-2xl font-bold text-REELTeal">
                            Infective Rhinitis Review Complete
                          </h4>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/*Red flag no infective identified alert*/}
            <EndServiceModel
              open={showNoInfectiveIdentified}
              showEndServiceAndRef={false}
              showOTCConsultAndRef={false}
              title="Infective Rhinitis Alert"
              className="w-[50%]"
              content={() => <p>No Infective Rhinitis Identified</p>}
              handleClose={() => {
                setContinueService(false);
                setShowNoInfectiveIdentified(false);
                setAlertConfirmation(0);
                setAlertExplanation("");
              }}
              handleContinue={(ref) => {
                setAlertConfirmation(0);
                setAlertExplanation("");
                if (ref !== undefined) {
                  setShowRef(ref);
                }
                setClinicalAnalysis(true);
                setShowNoInfectiveIdentified(false);
                setCompleteSSAlert(true);
              }}
            />

            {/* Red Flag Medical Condition Alerts */}
            <EndServiceModel
              open={openMultiConditionDialog}
              title="Infective Signs & Symptoms Alert"
              content={() => alertContent}
              handleClose={() => {
                setAlertConfirmation(0);
                setAlertExplanation("");
                setContinueService(false);
                setOpenMultiConditionDialog(false);
              }}
              handleContinue={(ref, exp) => {
                setAlertConfirmation(
                  ref
                    ? AlertConfirmationTypes.ContinueWithRefer
                    : AlertConfirmationTypes.ContinueWithNoReferral
                );
                setAlertExplanation(exp);
                if (ref !== undefined) {
                  setShowRef(ref);
                }
                setOpenMultiConditionDialog(false);
                updateStepsVisibility(false);
                setContinueService(true);
                setClinicalAnalysis(true);
                setCompleteSSAlert(true);
              }}
              handleEndService={() => {
                setShowRef(true);
                setAlertConfirmation(AlertConfirmationTypes.EndService);
                setAlertExplanation("");
                setOpenMultiConditionDialog(false);
                updateStepsVisibility(true);
                setClickedEndService(true);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] 
              ${!completeSSAlert && "opacity-40 pointer-events-none"}`}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Infective;
